import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  Divider,
  Flex,
  Heading,
  Image,
  Spacer,
  Text,
} from '@chakra-ui/react'
import { DocumentReference, getDoc, updateDoc } from 'firebase/firestore'
import React, { useContext, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ScoresContext } from '../routes/ScoreListing'
import ScoreDesc from './ScoreDesc'
import ScoreForm from './ScoreForm'
import { ScoreWithRef } from '../types'

import { useMachine } from '@xstate/react'
import createScoreMachine from '../logic/scoreMachine'
import { States, Events, Tags } from '../logic/scoreMachineConstants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'

// Define form data type
interface FormData {
  artist_song: string
  path: string
  accuracy: string | number
  longestStreak: string | number
  absoluteScore: string | number
}

export const capitalizedPath = (str: string): string => {
  if (str) {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }
  return str
}

interface ScoreProps {
  score: ScoreWithRef
}

function Score({ score }: ScoreProps) {
  const context = useContext(ScoresContext)

  const scoreMachine = useMemo(() => createScoreMachine(score), [score]) // otherwise, infinite loop re-rendering
  const [scoreState, send] = useMachine(scoreMachine)
  const [saving, setSaving] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>()

  // Se il contesto non è definito, mostriamo un messaggio di caricamento
  if (!context) {
    return <div>Loading...</div>
  }

  const { scores, setScores } = context

  const updateScoresStatus = async (ref: DocumentReference) => {
    const updatedScore = await getDoc(ref)
    const scoreToReplace = Object.assign({}, updatedScore.data(), {
      ref: updatedScore.ref,
    }) as ScoreWithRef
    const newScores = scores.map((obj: ScoreWithRef) => {
      if (obj.uniqueId === scoreToReplace.uniqueId) {
        return scoreToReplace
      }
      return obj
    })
    setScores(newScores)
  }

  const onSubmitEdit = async (data: FormData) => {
    let inputArtist, inputSong
    ;[inputArtist, inputSong] = data.artist_song.split('---')

    const editBody = {
      lastApprovedBy: 'todo', // TODO: auth
      lastApprovedDate: Date(),
      inputArtist: inputArtist,
      inputSong: inputSong,
      inputLevel: null,
      inputPath: data.path,
      lastEditedBy: 'todo',
      inputAccuracy: data.accuracy,
      inputLongestStreak: data.longestStreak,
      inputAbsoluteScore: data.absoluteScore,
      status: States.MANUALLY_APPROVED,
    } // add the accuracy as input???

    await updateDoc(score.ref, editBody)
    await updateScoresStatus(score.ref)
    setSaving(false)
    send({ type: Events.MANUALLY_APPROVE })
  }

  const approve = async (event: React.MouseEvent) => {
    const approveBody = {
      lastApprovedBy: 'todo',
      lastApprovedDate: Date(),
      status: States.MANUALLY_APPROVED,
    }

    await updateDoc(score.ref, approveBody)

    await updateScoresStatus(score.ref)

    setSaving(false)
    send({ type: Events.MANUALLY_APPROVE })
  }

  const markInvalid = async (event: React.MouseEvent) => {
    const approveBody = {
      lastApprovedBy: 'todo',
      lastApprovedDate: Date(),
      status: States.INVALID_SCORE,
    }

    await updateDoc(score.ref, approveBody)

    await updateScoresStatus(score.ref)

    setSaving(false)
    send({ type: Events.INVALIDATE })
  }

  const edit = async (event: React.MouseEvent) => {
    send({ type: Events.EDIT })
  }

  const goBack = async (event: React.MouseEvent) => {
    send({ type: Events.GO_BACK })
  }

  const removePushed = async (event: React.MouseEvent) => {
    await updateDoc(score.ref, {
      status: States.MANUALLY_APPROVED,
    })

    await updateScoresStatus(score.ref)

    setSaving(false)
    send({ type: Events.MANUALLY_APPROVE }) // TODO: incorrect, should be the previous state
  }

  const dynamicKey = Object.keys(scoreState.getMeta())[0]
  const styleProps = scoreState.getMeta()[dynamicKey]['cssBorder'] || { border: '5px solid white' }

  return (
    <form onSubmit={handleSubmit(onSubmitEdit)}>
      <Card maxW="m" {...styleProps} minH="830">
        <CardBody>
          <Flex>
            <a href={score.link} target="_blank" rel="noreferrer">
              <Heading as="h4" size="md">
                #{score.ix}
              </Heading>
            </a>
            <Spacer />
            <Text>
              {score.author}
              {/* {format(score.datetime.toDate(), 'yyyy-MM-dd HH:mm')} */}
            </Text>
          </Flex>
          <Flex direction="column" align="center" justify="center">
            <a href={score.link} target="_blank" rel="noreferrer">
              <Image
                sx={{ height: '65vh', maxWidth: '100%' }}
                src={score.image}
                alt="Green double couch with wooden legs"
                borderRadius="lg"
                objectFit="contain"
              />
            </a>
          </Flex>
          {scoreState.matches(States.EDITING) && (
            <ScoreForm score={score} register={register} errors={errors} />
          )}
          {!scoreState.matches(States.EDITING) && (
            <ScoreDesc score={score} scoreState={scoreState} />
          )}
          {/* {editing ? <ScoreForm score={score} register={register} errors={errors} /> : <ScoreDesc score={score} />} */}
        </CardBody>
        <Divider />
        <CardFooter>
          <Spacer />
          <ButtonGroup spacing="2">
            {scoreState.matches(States.PUSHED) && (
              <>
                <Button variant="solid" colorScheme="yellow" onClick={removePushed}>
                  {saving ? (
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                  ) : (
                    <>Remove Pushed</>
                  )}
                </Button>
              </>
            )}
            {scoreState.hasTag(Tags.EDITABLE) && (
              <>
                {/* <Button variant="ghost" colorScheme="red" onClick={() => setEditing(true)}> */}
                <Button variant="outline" colorScheme="red" onClick={edit}>
                  Edit
                </Button>
              </>
            )}
            {scoreState.hasTag(Tags.APPROVABLE) && (
              <>
                <Button variant="solid" colorScheme="green" onClick={approve}>
                  {saving ? <FontAwesomeIcon icon={faSpinner} className="fa-spin" /> : <>Approve</>}
                </Button>
              </>
            )}

            {scoreState.matches(States.EDITING) && (
              <>
                <Button variant="ghost" colorScheme="blackAlpha" onClick={goBack}>
                  Cancel
                </Button>
                <Button variant="outline" colorScheme="red" onClick={markInvalid}>
                  {saving ? (
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                  ) : (
                    <>Mark Invalid</>
                  )}
                </Button>
                <Button
                  variant="outline"
                  colorScheme="green"
                  type="submit"
                  onClick={() => setSaving(true)}
                >
                  {saving ? (
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                  ) : (
                    <>Save &amp; Approve</>
                  )}
                </Button>
              </>
            )}
          </ButtonGroup>
        </CardFooter>
      </Card>
    </form>
  )
}

export default Score
